import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Card, Button, Tooltip } from 'antd';
import { Square, CheckCircle } from 'lucide-react';
import { DeleteOutlined, WarningOutlined } from '@ant-design/icons';

interface EndNodeData {
  label: string;
  isExecuting?: boolean;
  isCompleted?: boolean;
  onDelete?: () => void;
  isUnlinked?: boolean;
}

const EndNode: React.FC<NodeProps<EndNodeData>> = ({ data, selected }) => {
  const { label, isExecuting, isCompleted, onDelete, isUnlinked } = data;

  const getCardStyle = () => {
    if (isExecuting) {
      return {
        borderColor: '#ff4d4f',
        boxShadow: '0 0 10px rgba(255, 77, 79, 0.5)',
        animation: 'pulse 1.5s ease-in-out infinite'
      };
    }
    if (isCompleted) {
      return {
        borderColor: '#52c41a',
        backgroundColor: '#f6ffed'
      };
    }
    if (selected) {
      return {
        borderColor: '#722ed1',
        boxShadow: '0 0 0 2px rgba(114, 46, 209, 0.2)'
      };
    }
    return {
      borderColor: '#ff4d4f'
    };
  };

  return (
    <>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: '#ff4d4f' }}
      />
      
      <Card
        size="small"
        style={{
          width: 160,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          position: 'relative',
          ...getCardStyle()
        }}
        className="end-node"
      >
        {/* Caution Icon for Unlinked Nodes */}
        {isUnlinked && (
          <Tooltip title="This node is not connected to any other nodes">
            <WarningOutlined
              style={{
                position: 'absolute',
                top: '4px',
                left: '4px',
                color: '#faad14',
                fontSize: '14px',
                zIndex: 10,
                backgroundColor: 'white',
                borderRadius: '50%',
                padding: '2px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }}
            />
          </Tooltip>
        )}
        
        {/* Delete Button */}
        {onDelete && (
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              width: '20px',
              height: '20px',
              fontSize: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              opacity: 0.7,
              transition: 'opacity 0.2s'
            }}
            className="node-delete-btn"
          />
        )}
        <div className="flex items-center space-x-2">
          <div className="flex-shrink-0">
            <div className={`p-2 rounded-full ${isCompleted ? 'bg-green-100' : 'bg-red-100'}`}>
              {isCompleted ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <Square className="h-4 w-4 text-red-600" />
              )}
            </div>
          </div>
          
          <div className="flex-1">
            <h4 className="text-sm font-medium text-gray-900">
              {label}
            </h4>
            
            {isExecuting && (
              <div className="mt-1">
                <div className="flex items-center space-x-1">
                  <div className="animate-spin h-2 w-2 border border-red-600 border-t-transparent rounded-full" />
                  <span className="text-xs text-red-600">Finishing...</span>
                </div>
              </div>
            )}
            
            {isCompleted && (
              <div className="mt-1">
                <span className="text-xs text-green-600">Completed</span>
              </div>
            )}
          </div>
        </div>
      </Card>
      
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
        
        .end-node:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        }

        .end-node .node-delete-btn {
          opacity: 0;
          transition: opacity 0.2s;
        }

        .end-node:hover .node-delete-btn {
          opacity: 1;
        }

        .node-delete-btn:hover {
          background-color: #ff4d4f !important;
          color: white !important;
        }
      `}</style>
    </>
  );
};

export default EndNode;